export const EXPORT_PADDING = 24; // fixed left/right/bottom padding around the schema, at full export resolution
export const PREVIEW_MAX_WIDTH = 420; // on-screen live-preview box — display concern only, doesn't affect the real capture
export const PREVIEW_MAX_HEIGHT = 300;

/**
 * Fit-to-bounds scale/translate for an export canvas of the given size — same
 * idea as UmlPreviewDlgHelpers.computeFitTransform, but width/height are
 * user-configurable here (not a fixed thumbnail), and paddingTop is a
 * separate, adjustable value: TOP-anchored (not centered like THUMB_PADDING),
 * so raising it pushes the schema down — e.g. to leave room for a title
 * added afterwards outside this app.
 * @param {{minX:number, minY:number, width:number, height:number}|null} bounds
 * @param {{width:number, height:number, paddingTop:number}} settings - target canvas size + top padding
 * @returns {string} CSS transform value
 */
export function computeExportFitTransform(bounds, { width, height, paddingTop }) {
    if (!bounds) return 'none';
    const availableW = width - EXPORT_PADDING * 2;
    const availableH = height - paddingTop - EXPORT_PADDING;
    // No `,1` cap here (unlike UmlPreviewDlgHelpers.computeFitTransform) — this is a
    // configurable export canvas, not a fixed small thumbnail, so the schema SHOULD scale
    // up to fill it. html-to-image rasterizes the DOM clone at the container's full
    // resolution, so the CSS scale() renders crisp — no upscale blur like a stretched bitmap.
    const scale = Math.min(availableW / bounds.width, availableH / bounds.height);
    // Center horizontally within the available width (same idea as THUMB_PADDING centering),
    // but vertically anchor right below paddingTop instead of centering — paddingTop IS the
    // vertical position control here, so it must not get diluted by extra auto-centering.
    const extraX = availableW - bounds.width * scale;
    const panX = EXPORT_PADDING + extraX / 2 - bounds.minX * scale;
    const panY = paddingTop - bounds.minY * scale;
    return `translate(${panX}px, ${panY}px) scale(${scale})`;
}

/**
 * Fits a {width,height} export-canvas aspect ratio inside a max on-screen
 * preview area, preserving aspect ratio — pure display concern for the live
 * preview box, entirely separate from computeExportFitTransform above (which
 * always operates in real export-resolution pixels, whatever size the
 * on-screen box ends up being).
 * @param {number} width
 * @param {number} height
 * @param {number} maxW
 * @param {number} maxH
 * @returns {{boxW: number, boxH: number, previewScale: number}}
 */
export function computePreviewBoxSize(width, height, maxW = PREVIEW_MAX_WIDTH, maxH = PREVIEW_MAX_HEIGHT) {
    const previewScale = Math.min(maxW / width, maxH / height, 1);
    return { boxW: width * previewScale, boxH: height * previewScale, previewScale };
}
